import type {
  FeatureKey,
  PipelineCheckResult,
  SpectralAnalysisSnapshot,
} from '../types';
import {
  DELTA_TOLERANCE,
  EXPECTED_ARTIFACT_METHOD,
  EXPECTED_DETREND_LAMBDA,
  EXPECTED_DETREND_METHOD,
  EXPECTED_NN_SOURCE,
  EXPECTED_PREPROCESSING_VERSION,
  EXPECTED_RESAMPLE_HZ,
  EXPECTED_RMSSD_SOURCE,
  EXPECTED_SPECTRAL_PATH_PRIMARY,
  PIPELINE_SPEC,
} from './constants';

const FEATURE_KEYS: FeatureKey[] = ['HR', 'HRV', 'LF', 'HF'];

const pass = (id: string, message: string): PipelineCheckResult => ({
  id,
  passed: true,
  message,
});

const fail = (id: string, message: string): PipelineCheckResult => ({
  id,
  passed: false,
  message,
});

const approxEqual = (
  left: number,
  right: number,
  tolerance = DELTA_TOLERANCE
) => Math.abs(left - right) <= tolerance;

export const runDeterministicChecks = (
  snapshot: SpectralAnalysisSnapshot
): PipelineCheckResult[] => {
  const checks: PipelineCheckResult[] = [];

  const selectedCount = snapshot.selectedSessions.length;
  const includedCount = snapshot.includedSessions.length;
  const excludedCount = snapshot.excludedSessions.length;
  const summary = snapshot.groupSummary;

  if (
    summary.totalSessions === selectedCount &&
    summary.includedSessions === includedCount &&
    summary.excludedSessions === excludedCount
  ) {
    checks.push(
      pass(
        'group-summary-counts',
        `Riepilogo gruppo coerente: ${includedCount} sessioni incluse, ${excludedCount} escluse su ${selectedCount} selezionate.`
      )
    );
  } else {
    checks.push(
      fail(
        'group-summary-counts',
        `Riepilogo gruppo incoerente: attese ${includedCount} incluse e ${excludedCount} escluse, ma il summary riporta ${summary.includedSessions}/${summary.excludedSessions}.`
      )
    );
  }

  const outlierCount = snapshot.includedSessions.filter(
    item => item.spectral.outlier
  ).length;
  if (outlierCount === summary.outlierCount) {
    checks.push(
      pass(
        'outlier-count',
        `Conteggio outlier coerente: ${outlierCount} sessioni segnalate come outlier.`
      )
    );
  } else {
    checks.push(
      fail(
        'outlier-count',
        `Conteggio outlier incoerente: il summary indica ${summary.outlierCount}, ma ne risultano ${outlierCount}.`
      )
    );
  }

  if (includedCount === 0) {
    checks.push(
      fail(
        'included-sessions',
        'Nessuna sessione inclusa: impossibile verificare la pipeline spettrale.'
      )
    );
    return checks;
  }

  snapshot.includedSessions.forEach(item => {
    const sessionId = item.validation.sessionId;
    const prefix = `session-${sessionId}`;

    FEATURE_KEYS.forEach(feature => {
      const metrics = item.validation.metrics[feature];
      const expectedDelta = metrics.exposure.mean - metrics.baseline.mean;
      if (approxEqual(metrics.delta, expectedDelta)) {
        checks.push(
          pass(
            `${prefix}-${feature}-delta`,
            `Sessione ${sessionId}, ${feature}: variazione pre/post VR coerente (${metrics.delta.toFixed(2)}).`
          )
        );
      } else {
        checks.push(
          fail(
            `${prefix}-${feature}-delta`,
            `Sessione ${sessionId}, ${feature}: variazione registrata (${metrics.delta.toFixed(2)}) non coincide con la differenza tra medie pre-VR e post-VR (attesa ≈ ${expectedDelta.toFixed(2)}).`
          )
        );
      }
    });

    const counts = item.validation.preprocessingCounts;
    const shrinkMonotonic =
      counts.originalPoints >= counts.afterAlignment &&
      counts.afterAlignment >= counts.afterDedup;
    const gapHandlingCoherent =
      counts.afterGapHandling === counts.afterDedup + counts.interpolatedPoints;

    if (shrinkMonotonic && gapHandlingCoherent) {
      checks.push(
        pass(
          `${prefix}-preprocessing-counts`,
          counts.interpolatedPoints > 0
            ? `Sessione ${sessionId}: preprocessing coerente (${counts.interpolatedPoints} campioni interpolati su ${counts.afterGapHandling} totali).`
            : `Sessione ${sessionId}: preprocessing coerente (${counts.originalPoints} campioni, nessuna interpolazione).`
        )
      );
    } else {
      checks.push(
        fail(
          `${prefix}-preprocessing-counts`,
          `Sessione ${sessionId}: i conteggi di preprocessing non sono coerenti tra allineamento, deduplicazione e gestione gap.`
        )
      );
    }

    const summary = item.spectral.preprocessingSummary;
    const hrvMeta = item.validation.hrvPreprocessing;

    if (summary.preprocessingVersion === EXPECTED_PREPROCESSING_VERSION) {
      checks.push(
        pass(
          `${prefix}-hrv-preprocessing`,
          `Sessione ${sessionId}: pipeline HRV preprocessing attiva (Polar 360, tachogramma da HR).`
        )
      );
    } else {
      checks.push(
        fail(
          `${prefix}-hrv-preprocessing`,
          `Sessione ${sessionId}: pipeline HRV preprocessing non conforme o assente.`
        )
      );
    }

    if (
      summary.nnSource === EXPECTED_NN_SOURCE &&
      hrvMeta.nnSource === EXPECTED_NN_SOURCE
    ) {
      checks.push(
        pass(
          `${prefix}-nn-source`,
          `Sessione ${sessionId}: tachogramma NN derivato da HR (proxy Polar 360 @ 1 Hz).`
        )
      );
    } else {
      checks.push(
        fail(
          `${prefix}-nn-source`,
          `Sessione ${sessionId}: sorgente NN non documentata come hr-derived-polar360.`
        )
      );
    }

    if (
      summary.detrendMethod === EXPECTED_DETREND_METHOD &&
      summary.detrendLambda === EXPECTED_DETREND_LAMBDA &&
      hrvMeta.detrendLambda === EXPECTED_DETREND_LAMBDA
    ) {
      checks.push(
        pass(
          `${prefix}-detrend`,
          `Sessione ${sessionId}: detrend smooth priors (λ=${EXPECTED_DETREND_LAMBDA}).`
        )
      );
    } else {
      checks.push(
        fail(
          `${prefix}-detrend`,
          `Sessione ${sessionId}: detrend non conforme (atteso smooth priors λ=${EXPECTED_DETREND_LAMBDA}).`
        )
      );
    }

    if (
      summary.resampleHz === EXPECTED_RESAMPLE_HZ &&
      hrvMeta.resampleHz === EXPECTED_RESAMPLE_HZ
    ) {
      checks.push(
        pass(
          `${prefix}-resample`,
          `Sessione ${sessionId}: ricampionamento a ${EXPECTED_RESAMPLE_HZ} Hz prima dell'analisi in frequenza.`
        )
      );
    } else {
      checks.push(
        fail(
          `${prefix}-resample`,
          `Sessione ${sessionId}: frequenza di ricampionamento non conforme (attesa ${EXPECTED_RESAMPLE_HZ} Hz).`
        )
      );
    }

    if (
      summary.correctedBeatPercentPassed &&
      hrvMeta.correctedBeatPercentPassed &&
      summary.correctedBeatPercentBaseline / 100 <=
        PIPELINE_SPEC.maxCorrectedBeatRatio &&
      summary.correctedBeatPercentExposure / 100 <=
        PIPELINE_SPEC.maxCorrectedBeatRatio
    ) {
      checks.push(
        pass(
          `${prefix}-artifact-gate`,
          `Sessione ${sessionId}: correzione artefatti entro soglia Kubios 5% (baseline ${summary.correctedBeatPercentBaseline.toFixed(2)}%, exposure ${summary.correctedBeatPercentExposure.toFixed(2)}%).`
        )
      );
    } else {
      checks.push(
        fail(
          `${prefix}-artifact-gate`,
          `Sessione ${sessionId}: soglia correzione artefatti non superata o non registrata.`
        )
      );
    }

    if (
      summary.spectralPathPrimary === EXPECTED_SPECTRAL_PATH_PRIMARY &&
      summary.rmssdSource === EXPECTED_RMSSD_SOURCE &&
      summary.artifactMethod === EXPECTED_ARTIFACT_METHOD
    ) {
      checks.push(
        pass(
          `${prefix}-spectral-path`,
          `Sessione ${sessionId}: analisi spettrale primaria su tachogramma NN ricostruito; RMSSD da ΔRR.`
        )
      );
    } else {
      checks.push(
        fail(
          `${prefix}-spectral-path`,
          `Sessione ${sessionId}: percorso spettrale primario o sorgente RMSSD non conformi al protocollo HRV preprocessing.`
        )
      );
    }

    const baselineRmssd = item.validation.metrics.HRV.baseline.rmssd;
    const exposureRmssd = item.validation.metrics.HRV.exposure.rmssd;
    if (
      typeof baselineRmssd === 'number' &&
      baselineRmssd > 0 &&
      typeof exposureRmssd === 'number' &&
      exposureRmssd > 0
    ) {
      checks.push(
        pass(
          `${prefix}-rmssd`,
          `Sessione ${sessionId}: RMSSD pre-VR ${baselineRmssd.toFixed(2)} ms, post-VR ${exposureRmssd.toFixed(2)} ms (da ΔRR sul tachogramma NN).`
        )
      );
    } else {
      checks.push(
        fail(
          `${prefix}-rmssd`,
          `Sessione ${sessionId}: RMSSD mancante o non positivo sui segmenti pre/post VR.`
        )
      );
    }

    if (
      summary.filterApplied.includes('smooth priors') &&
      summary.fftMethod.includes(`${EXPECTED_RESAMPLE_HZ} Hz`)
    ) {
      checks.push(
        pass(
          `${prefix}-fft-metadata`,
          `Sessione ${sessionId}: Welch su serie NN a ${EXPECTED_RESAMPLE_HZ} Hz registrato nei metadati.`
        )
      );
    } else {
      checks.push(
        fail(
          `${prefix}-fft-metadata`,
          `Sessione ${sessionId}: metadati analisi spettrale HRV preprocessing incompleti (Welch 4 Hz / detrend).`
        )
      );
    }

    const segmentLabel = { baseline: 'pre-VR', exposure: 'post-VR' } as const;

    (['baseline', 'exposure'] as const).forEach(segment => {
      const info = item.validation.segments[segment];
      if (
        info.valid &&
        info.durationSec !== PIPELINE_SPEC.segmentDurationSeconds
      ) {
        checks.push(
          fail(
            `${prefix}-${segment}-duration`,
            `Sessione ${sessionId}, segmento ${segmentLabel[segment]}: durata ${info.durationSec}s invece dei ${PIPELINE_SPEC.segmentDurationSeconds}s attesi (5 min).`
          )
        );
      } else if (info.valid) {
        checks.push(
          pass(
            `${prefix}-${segment}-duration`,
            `Sessione ${sessionId}, segmento ${segmentLabel[segment]}: valido, durata ${info.durationSec}s.`
          )
        );
      }
    });

    const psdLength = item.spectral.psd.baseline.frequencies.length;
    if (psdLength > 0) {
      checks.push(
        pass(
          `${prefix}-psd-present`,
          `Sessione ${sessionId}: densità spettrale di potenza calcolata (${psdLength} bande di frequenza).`
        )
      );
    } else {
      checks.push(
        fail(
          `${prefix}-psd-present`,
          `Sessione ${sessionId}: densità spettrale di potenza pre-VR assente o vuota.`
        )
      );
    }
  });

  return checks;
};
